import {
  useModals,
  Modal,
  ModalProps,
  Box,
  Button,
  Input,
  Select,
  SelectItem,
  Stack,
} from "@skailer-com/uikit";
import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { getEmailPlaceholderOptions } from "@/components/rich-editor/rich-editor.types.ts";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useTemplateSave } from "@/queries/use-templates.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiTemplate,
  ApiTemplateSavePayload,
  ApiTemplateType,
  ApiTemplateVisibility,
} from "@/types/template.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface EmailTemplateEditModalProps extends ModalProps {
  emailTemplate?: ApiTemplate;
  templateType?: ApiTemplateType;
  readOnly?: boolean;
}

export function TemplateEditModal({
  emailTemplate,
  templateType,
  readOnly = false,
  ...props
}: EmailTemplateEditModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { data: employeeMe } = useEmployeeMe();

  const canChangeVisibility = !emailTemplate || emailTemplate.owner.id === employeeMe?.id;

  const emailPlaceholderOptions = getEmailPlaceholderOptions(t);

  const emailTemplateSchema = z.object({
    title: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
    subject: z.string().nullable(),
    body: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
    is_private: z.boolean({ error: i18n.t("errors:field_required") }),
  });

  type EmailTemplateSchema = z.infer<typeof emailTemplateSchema>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EmailTemplateSchema>({
    mode: "onSubmit",
    resolver: zodResolver(emailTemplateSchema),
    values: {
      title: emailTemplate?.title ?? "",
      subject: emailTemplate?.subject ?? null,
      body: emailTemplate?.body ?? "",
      is_private: emailTemplate?.is_private || false,
    },
  });

  const templateSave = useTemplateSave(control);
  const currentType = emailTemplate?.template_type || templateType || ApiTemplateType.email;
  const onSubmit = handleSubmit(async (data) => {
    try {
      const payload: ApiTemplateSavePayload = {
        title: data.title,
        subject: data.subject,
        body: data.body,
        is_private: data.is_private,
        template_type: currentType,
      };
      const savedData = await templateSave.mutateAsync({
        id: emailTemplate?.id,
        payload: payload,
      });
      if (emailTemplate) {
        analyticsService.trackEvent(analyticEvents.templates.edited, {
          [analyticProperties.id]: emailTemplate.id,
        });
      } else {
        analyticsService.trackEvent(analyticEvents.templates.created, {
          [analyticProperties.id]: savedData.id,
        });
      }
      close(id);
    } catch {
      return;
    }
  });

  const emailTemplateVisibilityOptions = useMemo(() => {
    return [
      { id: ApiTemplateVisibility.public, title: t("core:email_template_public") },
      { id: ApiTemplateVisibility.private, title: t("core:email_template_private") },
    ] as SelectItem[];
  }, []);

  return (
    <>
      <Modal
        {...props}
        title={
          currentType == ApiTemplateType.email
            ? t("core:email_template")
            : t("core:message_template")
        }
        withCloser
        size={"lg"}
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            {readOnly ? (
              <Button
                size={"lg"}
                onClick={() => {
                  close(id);
                }}
              >
                {t("common:close")}
              </Button>
            ) : (
              <Button
                type={"submit"}
                size={"lg"}
                isLoading={templateSave.isPending}
                onClick={onSubmit}
              >
                {t("common:save")}
              </Button>
            )}
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Input
            required={true}
            label={t("common:title")}
            autoFocus={true}
            type="text"
            {...register("title")}
            error={errors.title?.message}
            readOnly={readOnly}
          />
          {currentType == ApiTemplateType.email &&
            (readOnly ? (
              <Input
                required={true}
                label={t("core:email_subject")}
                type={"text"}
                {...register("subject", {
                  value: undefined,
                })}
                error={errors.subject?.message}
                readOnly={readOnly}
              />
            ) : (
              <InputWithVariableInsert
                required={true}
                label={t("core:email_subject")}
                type={"text"}
                {...register("subject", {
                  value: undefined,
                })}
                error={errors.subject?.message}
                variableOptions={emailPlaceholderOptions}
              />
            ))}

          {!readOnly && canChangeVisibility && (
            <Stack>
              <Controller
                control={control}
                name="is_private"
                render={({ field: { value, onChange } }) => (
                  <Select
                    readOnly={readOnly}
                    value={value ? ApiTemplateVisibility.private : ApiTemplateVisibility.public}
                    onChange={(newValue) => onChange(newValue === ApiTemplateVisibility.private)}
                    options={emailTemplateVisibilityOptions}
                    emptyTitle={t("common:visibility")}
                  />
                )}
              />
            </Stack>
          )}

          <Box className={"pt-(--gap-md)"}>
            <Controller
              render={({ field: { value, onChange } }) => (
                <RichEditor
                  readOnly={readOnly}
                  value={value || ""}
                  onChange={onChange}
                  placeholderOptions={emailPlaceholderOptions}
                  error={errors.body?.message}
                  placeholder={t("core:email_template_enter_body")}
                />
              )}
              control={control}
              name={"body"}
            />
          </Box>
        </form>
      </Modal>
    </>
  );
}
